/**
 * Regras de negociação para Consórcio
 * 
 * Baseado nas regras definidas em regras.txt:
 * - C_PCTIPD = 1 é parcela
 * - C_PCTIPD = 2 é diferença de parcela
 * 
 * Tipos de status:
 * - NAO CONTEMPLADO
 * - CONTEMPLADO SEM BEM
 * - CONTEMPLADO COM BEM
 */

/**
 * Normaliza o tipo de status para comparação
 * Trata casos onde o JSON pode vir com escapes ou variações de escrita
 * @param {string} type - Tipo de status
 * @returns {string} Tipo normalizado ou null
 */
function normalizeMediationType(type) {
  if (!type || type === "unknown") {
    return null;
  }
  
  // Converte para string e normaliza escapes de JSON
  // Remove escapes de barra: \/ -> / (caso venha como string literal)
  // O JavaScript normalmente já faz isso no JSON.parse(), mas garantimos aqui também
  let normalized = String(type)
    .replace(/\\\//g, "/")      // Remove escape de barra: \/ -> /
    .replace(/\\\\/g, "\\")     // Remove escape de backslash: \\ -> \
    .toUpperCase()
    .trim();
  
  // Mapeia variações possíveis do tipo (novas labels)
  // Verifica "NAO CONTEMPLADO" ou "NÃO CONTEMPLADO"
  if (normalized.includes("NAO CONTEMPLADO") || normalized.includes("NÃO CONTEMPLADO")) {
    return "NAO CONTEMPLADO";
  }
  
  // Verifica "CONTEMPLADO SEM BEM"
  if (normalized.includes("CONTEMPLADO SEM BEM")) {
    return "CONTEMPLADO SEM BEM";
  }
  
  // Verifica "CONTEMPLADO COM BEM"
  if (normalized.includes("CONTEMPLADO COM BEM")) {
    return "CONTEMPLADO COM BEM";
  }
  
  // Compatibilidade com labels antigas (caso ainda existam no sistema)
  // "MEDIACAO" ou "MEDIAÇÃO" -> verifica as variações antigas
  const hasMediacao = normalized.includes("MEDIACAO") || normalized.includes("MEDIAÇÃO");
  
  if (hasMediacao) {
    // Mapeia labels antigas para as novas
    if (normalized.includes("N/C")) {
      return "NAO CONTEMPLADO";
    }
    if (normalized.includes("S/BEM")) {
      return "CONTEMPLADO SEM BEM";
    }
    if (normalized.includes("CONT") && normalized.includes("BEM") && normalized.includes("C/")) {
      return "CONTEMPLADO COM BEM";
    }
  }
  
  return normalized;
}

/**
 * Verifica se deve ignorar as regras (quando type ou C_PCTIPD é "unknown")
 * @param {string} type - Tipo de mediação
 * @param {string} c_pctipd - Valor de C_PCTIPD
 * @returns {boolean} true se deve ignorar as regras
 */
function shouldIgnoreRules(type, c_pctipd) {
  return !type || type === "unknown" || !c_pctipd || c_pctipd === "unknown";
}

/**
 * Obtém o C_PCTIPD de uma parcela
 * @param {Object} installment - Parcela
 * @returns {string} C_PCTIPD da parcela
 */
function getInstallmentC_PCTIPD(installment) {
  return installment?.C_PCTIPD || installment?.c_pctipd || null;
}

/**
 * Obtém o tipo de mediação de uma parcela ou contrato
 * Se o contract não tiver type, tenta pegar da primeira parcela
 * @param {Object} installment - Parcela
 * @param {Object} contract - Contrato (opcional)
 * @returns {string} Tipo de mediação
 */
function getMediationType(installment, contract = null) {
  let type = installment?.type || contract?.type || null;
  
  // Se não encontrou o type e há um contract com installments, pega da primeira parcela
  if (!type && contract && contract.installments && contract.installments.length > 0) {
    type = contract.installments[0]?.type || null;
    console.log("[getMediationType] Type não encontrado no contract, pegando da primeira parcela:", type);
  }
  
  const normalized = normalizeMediationType(type);
  console.log("[getMediationType] type original:", type, "-> normalizado:", normalized);
  return normalized;
}

/**
 * Analisa as parcelas de um contrato e retorna informações sobre C_PCTIPD
 * @param {Array} installments - Array de parcelas
 * @returns {Object} Informações sobre as parcelas
 */
function analyzeInstallments(installments) {
  console.log("[analyzeInstallments] Iniciando análise de", installments?.length || 0, "parcelas");
  
  if (!installments || installments.length === 0) {
    console.log("[analyzeInstallments] Nenhuma parcela, retornando valores padrão");
    return {
      allType1: false,
      allType2: false,
      hasType1: false,
      hasType2: false,
      hasMixed: false,
      type1Count: 0,
      type2Count: 0,
      hasUnknown: false,
    };
  }

  // Log detalhado de cada parcela
  installments.forEach((inst, index) => {
    const c_pctipd = getInstallmentC_PCTIPD(inst);
    const type = getMediationType(inst);
    console.log(`[analyzeInstallments] Parcela ${index + 1}:`, {
      installmentId: inst.installmentId,
      installmentNumber: inst.installmentNumber,
      C_PCTIPD: c_pctipd,
      type: inst.type,
      typeNormalized: type
    });
  });

  const type1Installments = installments.filter(inst => getInstallmentC_PCTIPD(inst) === "1");
  const type2Installments = installments.filter(inst => getInstallmentC_PCTIPD(inst) === "2");
  const unknownInstallments = installments.filter(inst => 
    shouldIgnoreRules(getMediationType(inst), getInstallmentC_PCTIPD(inst))
  );

  const type1Count = type1Installments.length;
  const type2Count = type2Installments.length;
  const total = installments.length;
  
  console.log("[analyzeInstallments] Resultados:", {
    type1Count,
    type2Count,
    total,
    unknownCount: unknownInstallments.length
  });

  return {
    allType1: type1Count === total && total > 0,
    allType2: type2Count === total && total > 0,
    hasType1: type1Count > 0,
    hasType2: type2Count > 0,
    hasMixed: type1Count > 0 && type2Count > 0,
    type1Count,
    type2Count,
    hasUnknown: unknownInstallments.length > 0,
    type1Installments,
    type2Installments,
  };
}

/**
 * Verifica se pode desmarcar uma parcela baseado nas regras
 * @param {Object} installment - Parcela a ser desmarcada
 * @param {Object} contract - Contrato
 * @param {Array} checkedParcels - Parcelas atualmente selecionadas
 * @returns {Object} { canUncheck: boolean, reason: string }
 */
export function canUncheckInstallment(installment, contract, checkedParcels = []) {
  const mediationType = getMediationType(installment, contract);
  const c_pctipd = getInstallmentC_PCTIPD(installment);

  // REGRA CRÍTICA: Parcelas C_PCTIPD=2 (diferença de parcela) NUNCA podem ser desmarcadas
  // Independente do tipo de mediação ou qualquer outra regra
  if (c_pctipd === "2") {
    console.log("[canUncheckInstallment] C_PCTIPD=2 detectado - NUNCA pode desmarcar", {
      installmentNumber: installment.installmentNumber,
      installmentId: installment.installmentId
    });
    return { canUncheck: false, reason: "type2_cannot_be_unchecked" };
  }

  // Se type ou C_PCTIPD é "unknown", permite desmarcar livremente
  if (shouldIgnoreRules(mediationType, c_pctipd)) {
    return { canUncheck: true, reason: "unknown_type_or_c_pctipd" };
  }

  // Se não há tipo de mediação válido, permite desmarcar
  if (!mediationType) {
    return { canUncheck: true, reason: "no_mediation_type" };
  }

  const analysis = analyzeInstallments(contract.installments || []);

  // Se há parcelas com tipo unknown, permite desmarcar livremente
  if (analysis.hasUnknown) {
    return { canUncheck: true, reason: "has_unknown_installments" };
  }

  // Regras para NAO CONTEMPLADO
  if (mediationType === "NAO CONTEMPLADO") {
    if (analysis.allType1) {
      // NOVA REGRA: A parcela mais ATRASADA (menor installmentNumber) NUNCA pode ser desmarcada
      // As demais podem ser marcadas/desmarcadas livremente
      
      const allContractInstallments = contract.installments || [];
      
      // Se há apenas uma parcela no contrato, não pode desmarcar (deve permanecer selecionada)
      if (allContractInstallments.length === 1) {
        console.log("[canUncheckInstallment] NAO CONTEMPLADO - allType1: Apenas uma parcela, não pode desmarcar", {
          installmentNumber: installment.installmentNumber
        });
        return { canUncheck: false, reason: "single_installment_cannot_be_unchecked" };
      }
      
      // Encontra a parcela mais atrasada (menor installmentNumber)
      const sortedInstallments = [...allContractInstallments].sort((a, b) => 
        parseInt(a.installmentNumber) - parseInt(b.installmentNumber)
      );
      const oldestInstallment = sortedInstallments[0];
      
      // Se está tentando desmarcar a mais atrasada, não permite
      const isOldestInstallment = oldestInstallment && 
        oldestInstallment.installmentId === installment.installmentId;
      
      console.log("[canUncheckInstallment] NAO CONTEMPLADO - allType1:", {
        installmentNumber: installment.installmentNumber,
        installmentId: installment.installmentId,
        oldestInstallmentNumber: oldestInstallment?.installmentNumber,
        oldestInstallmentId: oldestInstallment?.installmentId,
        isOldestInstallment,
        canUncheck: !isOldestInstallment
      });
      
      if (isOldestInstallment) {
        return { canUncheck: false, reason: "oldest_installment_cannot_be_unchecked" };
      }
      
      // As demais parcelas podem ser desmarcadas livremente
      return { canUncheck: true, reason: "not_oldest_allowed" };
    }
    
    if (analysis.allType2) {
      // Não permite desmarcar nada
      return { canUncheck: false, reason: "all_type2_no_uncheck_allowed" };
    }
    
    if (analysis.hasMixed) {
      // Regra: Se há pelo menos uma C_PCTIPD=1 selecionada, a C_PCTIPD=2 DEVE estar selecionada
      // Se não há nenhuma C_PCTIPD=1 selecionada, pode ter apenas a C_PCTIPD=2
      
      const selectedType1 = checkedParcels.filter(p => 
        getInstallmentC_PCTIPD(p) === "1" &&
        contract.installments.some(inst => inst.installmentId === p.installmentId)
      );
      
      if (c_pctipd === "2") {
        // Tentando desmarcar C_PCTIPD=2
        // Só pode desmarcar se NÃO houver nenhuma C_PCTIPD=1 selecionada
        if (selectedType1.length > 0) {
          return { canUncheck: false, reason: "type2_must_be_selected_when_type1_selected" };
        }
        // Se não há C_PCTIPD=1 selecionada, pode desmarcar a C_PCTIPD=2
        return { canUncheck: true, reason: "type2_can_be_unchecked_when_no_type1" };
      }
      
      // Tentando desmarcar C_PCTIPD=1
      // Pode desmarcar livremente, mas se desmarcar todas as C_PCTIPD=1,
      // então a C_PCTIPD=2 também pode ser desmarcada (será tratado automaticamente)
      return { canUncheck: true, reason: "type1_can_be_unchecked" };
    }
  }

  // Regras para CONTEMPLADO SEM BEM
  if (mediationType === "CONTEMPLADO SEM BEM") {
    if (analysis.allType1) {
      // Permite desmarcar somente a mais recente (apenas uma parcela pode ser desmarcada)
      // A mais recente é a parcela com o MAIOR installmentNumber
      
      // Filtra apenas parcelas do mesmo contrato
      const contractInstallmentIds = (contract.installments || []).map(inst => inst.installmentId);
      const allContractInstallments = contract.installments || [];
      const relevantCheckedParcels = checkedParcels.filter(p => 
        contractInstallmentIds.includes(p.installmentId)
      );
      
      // Se não há parcelas selecionadas do contrato, não pode desmarcar
      if (relevantCheckedParcels.length === 0) {
        return { canUncheck: false, reason: "no_installments_selected" };
      }
      
      // CRÍTICO: Se já foi desmarcada uma parcela (não estão todas selecionadas),
      // então não pode desmarcar mais nenhuma
      const totalContractInstallments = allContractInstallments.length;
      const selectedCount = relevantCheckedParcels.length;
      
      // Se há apenas uma parcela no contrato, não pode desmarcar (deve permanecer selecionada)
      if (totalContractInstallments === 1) {
        console.log("[canUncheckInstallment] CONTEMPLADO SEM BEM - allType1: Apenas uma parcela, não pode desmarcar", {
          totalContractInstallments,
          selectedCount,
          installmentNumber: installment.installmentNumber
        });
        return { canUncheck: false, reason: "single_installment_cannot_be_unchecked" };
      }
      
      // Se já foi desmarcada pelo menos uma parcela, não pode desmarcar mais nenhuma
      if (selectedCount < totalContractInstallments) {
        console.log("[canUncheckInstallment] CONTEMPLADO SEM BEM - allType1: Já foi desmarcada uma parcela, não pode desmarcar mais", {
          totalContractInstallments,
          selectedCount,
          installmentNumber: installment.installmentNumber
        });
        return { canUncheck: false, reason: "only_one_can_be_unchecked" };
      }
      
      // Se todas estão selecionadas, então apenas a mais recente pode ser desmarcada
      // Ordena por installmentNumber em ordem decrescente (maior número = mais recente)
      const sortedChecked = [...relevantCheckedParcels].sort((a, b) => 
        parseInt(b.installmentNumber) - parseInt(a.installmentNumber)
      );
      const mostRecent = sortedChecked[0];
      
      // Só pode desmarcar se for exatamente a parcela mais recente
      const canUncheck = mostRecent && mostRecent.installmentId === installment.installmentId;
      
      console.log("[canUncheckInstallment] CONTEMPLADO SEM BEM - allType1:", {
        installmentNumber: installment.installmentNumber,
        installmentId: installment.installmentId,
        totalContractInstallments,
        selectedCount,
        relevantCheckedCount: relevantCheckedParcels.length,
        sortedCheckedNumbers: sortedChecked.map(p => p.installmentNumber),
        mostRecentNumber: mostRecent?.installmentNumber,
        mostRecentId: mostRecent?.installmentId,
        canUncheck
      });
      
      return {
        canUncheck,
        reason: canUncheck ? "most_recent_allowed" : "only_most_recent_can_be_unchecked"
      };
    }
    
    if (analysis.allType2) {
      // Não permite desmarcar nada
      return { canUncheck: false, reason: "all_type2_no_uncheck_allowed" };
    }
    
    if (analysis.hasMixed) {
      // Regra: Se há pelo menos uma C_PCTIPD=1 selecionada, a C_PCTIPD=2 DEVE estar selecionada
      // Se não há nenhuma C_PCTIPD=1 selecionada, pode ter apenas a C_PCTIPD=2
      
      const selectedType1 = checkedParcels.filter(p => 
        getInstallmentC_PCTIPD(p) === "1" &&
        contract.installments.some(inst => inst.installmentId === p.installmentId)
      );
      
      if (c_pctipd === "2") {
        // Tentando desmarcar C_PCTIPD=2
        // Só pode desmarcar se NÃO houver nenhuma C_PCTIPD=1 selecionada
        if (selectedType1.length > 0) {
          return { canUncheck: false, reason: "type2_must_be_selected_when_type1_selected" };
        }
        // Se não há C_PCTIPD=1 selecionada, pode desmarcar a C_PCTIPD=2
        return { canUncheck: true, reason: "type2_can_be_unchecked_when_no_type1" };
      }
      
      // Tentando desmarcar C_PCTIPD=1
      // Pode desmarcar livremente, mas se desmarcar todas as C_PCTIPD=1,
      // então a C_PCTIPD=2 também pode ser desmarcada (será tratado automaticamente)
      return { canUncheck: true, reason: "type1_can_be_unchecked" };
    }
  }

  // Regras para CONTEMPLADO COM BEM
  if (mediationType === "CONTEMPLADO COM BEM") {
    if (analysis.allType1) {
      // Permite desmarcar somente a mais recente (apenas uma parcela pode ser desmarcada)
      // A mais recente é a parcela com o MAIOR installmentNumber
      
      // Filtra apenas parcelas do mesmo contrato
      const contractInstallmentIds = (contract.installments || []).map(inst => inst.installmentId);
      const allContractInstallments = contract.installments || [];
      const relevantCheckedParcels = checkedParcels.filter(p => 
        contractInstallmentIds.includes(p.installmentId)
      );
      
      // Se não há parcelas selecionadas do contrato, não pode desmarcar
      if (relevantCheckedParcels.length === 0) {
        return { canUncheck: false, reason: "no_installments_selected" };
      }
      
      // CRÍTICO: Se já foi desmarcada uma parcela (não estão todas selecionadas),
      // então não pode desmarcar mais nenhuma
      const totalContractInstallments = allContractInstallments.length;
      const selectedCount = relevantCheckedParcels.length;
      
      // Se há apenas uma parcela no contrato, não pode desmarcar (deve permanecer selecionada)
      if (totalContractInstallments === 1) {
        console.log("[canUncheckInstallment] CONTEMPLADO COM BEM - allType1: Apenas uma parcela, não pode desmarcar", {
          totalContractInstallments,
          selectedCount,
          installmentNumber: installment.installmentNumber
        });
        return { canUncheck: false, reason: "single_installment_cannot_be_unchecked" };
      }
      
      // Se já foi desmarcada pelo menos uma parcela, não pode desmarcar mais nenhuma
      if (selectedCount < totalContractInstallments) {
        console.log("[canUncheckInstallment] CONTEMPLADO COM BEM - allType1: Já foi desmarcada uma parcela, não pode desmarcar mais", {
          totalContractInstallments,
          selectedCount,
          installmentNumber: installment.installmentNumber
        });
        return { canUncheck: false, reason: "only_one_can_be_unchecked" };
      }
      
      // Se todas estão selecionadas, então apenas a mais recente pode ser desmarcada
      // Ordena por installmentNumber em ordem decrescente (maior número = mais recente)
      const sortedChecked = [...relevantCheckedParcels].sort((a, b) => 
        parseInt(b.installmentNumber) - parseInt(a.installmentNumber)
      );
      const mostRecent = sortedChecked[0];
      
      // Só pode desmarcar se for exatamente a parcela mais recente
      const canUncheck = mostRecent && mostRecent.installmentId === installment.installmentId;
      
      console.log("[canUncheckInstallment] CONTEMPLADO COM BEM - allType1:", {
        installmentNumber: installment.installmentNumber,
        installmentId: installment.installmentId,
        totalContractInstallments,
        selectedCount,
        relevantCheckedCount: relevantCheckedParcels.length,
        sortedCheckedNumbers: sortedChecked.map(p => p.installmentNumber),
        mostRecentNumber: mostRecent?.installmentNumber,
        mostRecentId: mostRecent?.installmentId,
        canUncheck
      });
      
      return {
        canUncheck,
        reason: canUncheck ? "most_recent_allowed" : "only_most_recent_can_be_unchecked"
      };
    }
    
    if (analysis.allType2 || analysis.hasMixed) {
      // Não permite desmarcar nada
      return { canUncheck: false, reason: "all_type2_or_mixed_no_uncheck_allowed" };
    }
  }

  // Por padrão, permite desmarcar
  return { canUncheck: true, reason: "default_allowed" };
}

/**
 * Verifica se pode marcar uma parcela baseado nas regras
 * @param {Object} installment - Parcela a ser marcada
 * @param {Object} contract - Contrato
 * @returns {boolean} true se pode marcar
 */
export function canCheckInstallment(installment, contract) {
  const mediationType = getMediationType(installment, contract);
  const c_pctipd = getInstallmentC_PCTIPD(installment);

  // Se type ou C_PCTIPD é "unknown", permite marcar livremente
  if (shouldIgnoreRules(mediationType, c_pctipd)) {
    return true;
  }

  // Por padrão, permite marcar
  return true;
}

/**
 * Retorna todas as parcelas que devem ser selecionadas automaticamente ao carregar o componente
 * @param {Object} contract - Contrato
 * @returns {Array} Parcelas que devem ser selecionadas automaticamente
 */
export function getInitialAutoSelectInstallments(contract) {
  console.log("[getInitialAutoSelectInstallments] Iniciando - contract:", contract);
  console.log("[getInitialAutoSelectInstallments] contract.type:", contract?.type);
  console.log("[getInitialAutoSelectInstallments] contract.installments:", contract?.installments);
  
  const mediationType = getMediationType(null, contract);
  console.log("[getInitialAutoSelectInstallments] mediationType normalizado:", mediationType);

  const analysis = analyzeInstallments(contract.installments || []);
  console.log("[getInitialAutoSelectInstallments] Análise das parcelas:", {
    allType1: analysis.allType1,
    allType2: analysis.allType2,
    hasMixed: analysis.hasMixed,
    hasUnknown: analysis.hasUnknown,
    type1Count: analysis.type1Count,
    type2Count: analysis.type2Count,
    totalInstallments: contract.installments?.length || 0
  });

  // REGRA CRÍTICA: Parcelas C_PCTIPD=2 SEMPRE devem ser selecionadas automaticamente
  // Independente do tipo de mediação ou qualquer outra regra
  const type2Installments = (contract.installments || []).filter(inst => 
    getInstallmentC_PCTIPD(inst) === "2"
  );
  
  if (type2Installments.length > 0) {
    console.log("[getInitialAutoSelectInstallments] C_PCTIPD=2 encontradas - DEVEM ser selecionadas automaticamente:", type2Installments.length);
  }
  
  // Se type é "unknown", retorna apenas as parcelas C_PCTIPD=2 (que devem sempre ser selecionadas)
  if (!mediationType || mediationType === "unknown") {
    console.log("[getInitialAutoSelectInstallments] MediationType é unknown ou null, retornando apenas C_PCTIPD=2:", type2Installments.length);
    return [...type2Installments];
  }
  
  // Se há parcelas com tipo unknown, retorna apenas as parcelas C_PCTIPD=2
  if (analysis.hasUnknown) {
    console.log("[getInitialAutoSelectInstallments] Há parcelas unknown, retornando apenas C_PCTIPD=2:", type2Installments.length);
    return [...type2Installments];
  }

  // Regras para NAO CONTEMPLADO
  if (mediationType === "NAO CONTEMPLADO") {
    console.log("[getInitialAutoSelectInstallments] Tipo é NAO CONTEMPLADO");
    
    // Se todas as parcelas são C_PCTIPD=1: selecionar APENAS a mais atrasada (menor installmentNumber)
    // O usuário pode marcar as demais se quiser
    if (analysis.allType1) {
      const sortedInstallments = [...contract.installments].sort((a, b) => 
        parseInt(a.installmentNumber) - parseInt(b.installmentNumber)
      );
      const oldestInstallment = sortedInstallments[0];
      console.log("[getInitialAutoSelectInstallments] NAO CONTEMPLADO allType1 - selecionando apenas a mais atrasada:", oldestInstallment?.installmentNumber);
      return oldestInstallment ? [oldestInstallment] : [];
    }
    
    // Se todas as parcelas são C_PCTIPD=2: selecionar todas (não pode desmarcar nada)
    // Como não pode desmarcar, todas devem vir selecionadas automaticamente
    if (analysis.allType2) {
      console.log("[getInitialAutoSelectInstallments] Todas são C_PCTIPD=2, retornando todas as parcelas (não pode desmarcar):", contract.installments?.length);
      return [...contract.installments];
    }
    
    // Se há parcelas mistas (C_PCTIPD=1 e C_PCTIPD=2): C_PCTIPD=2 já vem selecionada
    // Deixa o usuário escolher as C_PCTIPD=1, mas C_PCTIPD=2 já deve estar selecionada
    if (analysis.hasMixed) {
      console.log("[getInitialAutoSelectInstallments] Parcelas mistas, selecionando apenas C_PCTIPD=2:", type2Installments.length);
      return [...type2Installments];
    }
  }
  
  // Regras para CONTEMPLADO SEM BEM
  if (mediationType === "CONTEMPLADO SEM BEM") {
    console.log("[getInitialAutoSelectInstallments] Tipo é CONTEMPLADO SEM BEM");
    
    // Se todas as parcelas são C_PCTIPD=1: selecionar todas (pode desmarcar apenas a mais recente depois)
    if (analysis.allType1) {
      console.log("[getInitialAutoSelectInstallments] Todas são C_PCTIPD=1, retornando todas as parcelas:", contract.installments?.length);
      return [...contract.installments];
    }
    
    // Se todas as parcelas são C_PCTIPD=2: selecionar todas (não pode desmarcar nada)
    // Como não pode desmarcar, todas devem vir selecionadas automaticamente
    if (analysis.allType2) {
      console.log("[getInitialAutoSelectInstallments] Todas são C_PCTIPD=2, retornando todas as parcelas (não pode desmarcar):", contract.installments?.length);
      return [...contract.installments];
    }
    
    // Se há parcelas mistas (C_PCTIPD=1 e C_PCTIPD=2): C_PCTIPD=2 já vem selecionada
    // Deixa o usuário escolher as C_PCTIPD=1, mas C_PCTIPD=2 já deve estar selecionada
    if (analysis.hasMixed) {
      console.log("[getInitialAutoSelectInstallments] Parcelas mistas, selecionando apenas C_PCTIPD=2:", type2Installments.length);
      return [...type2Installments];
    }
  }

  // Regras para CONTEMPLADO COM BEM
  if (mediationType === "CONTEMPLADO COM BEM") {
    console.log("[getInitialAutoSelectInstallments] Tipo é CONTEMPLADO COM BEM");
    
    // Se todas as parcelas são C_PCTIPD=1: selecionar todas (pode desmarcar apenas a mais recente depois)
    if (analysis.allType1) {
      console.log("[getInitialAutoSelectInstallments] Todas são C_PCTIPD=1, retornando todas as parcelas:", contract.installments?.length);
      return [...contract.installments];
    }
    
    // Se todas as parcelas são C_PCTIPD=2 ou mistas: selecionar todas (não pode desmarcar nada)
    // Como não pode desmarcar, todas devem vir selecionadas automaticamente
    if (analysis.allType2 || analysis.hasMixed) {
      console.log("[getInitialAutoSelectInstallments] Todas são C_PCTIPD=2 ou mistas, retornando todas as parcelas (não pode desmarcar):", contract.installments?.length);
      return [...contract.installments];
    }
  }

  // Por padrão, retorna pelo menos as parcelas C_PCTIPD=2 (que devem sempre ser selecionadas)
  console.log("[getInitialAutoSelectInstallments] Nenhuma regra específica aplicada, retornando C_PCTIPD=2:", type2Installments.length);
  return [...type2Installments];
}

/**
 * Verifica se deve selecionar automaticamente parcelas C_PCTIPD=2
 * quando há mais de um C_PCTIPD=1 selecionado (durante a interação do usuário)
 * @param {Object} contract - Contrato
 * @param {Array} checkedParcels - Parcelas atualmente selecionadas
 * @returns {Array} Parcelas que devem ser selecionadas automaticamente
 */
export function getAutoSelectInstallments(contract, checkedParcels = []) {
  const mediationType = getMediationType(null, contract);
  
  // Se type é "unknown", não aplica seleção automática
  if (!mediationType || mediationType === "unknown") {
    return [];
  }

  const analysis = analyzeInstallments(contract.installments || []);
  
  // Se há parcelas com tipo unknown, não aplica seleção automática
  if (analysis.hasUnknown) {
    return [];
  }

  // Aplica regra apenas para NAO CONTEMPLADO e CONTEMPLADO SEM BEM
  if (mediationType === "NAO CONTEMPLADO" || mediationType === "CONTEMPLADO SEM BEM") {
    if (analysis.hasMixed) {
      const selectedType1 = checkedParcels.filter(p => 
        getInstallmentC_PCTIPD(p) === "1" &&
        contract.installments.some(inst => inst.installmentId === p.installmentId)
      );
      
      // Se há pelo menos uma C_PCTIPD=1 selecionada, a C_PCTIPD=2 DEVE estar selecionada
      if (selectedType1.length > 0) {
        const type2Installments = analysis.type2Installments.filter(inst => 
          !checkedParcels.some(p => p.installmentId === inst.installmentId)
        );
        return type2Installments;
      }
    }
  }

  return [];
}

/**
 * Verifica se deve direcionar ao WhatsApp (mais de 30 dias de atraso em CONTEMPLADO COM BEM)
 * @param {Object} contract - Contrato
 * @returns {Object} { shouldRedirect: boolean, reason: string }
 */
export function shouldRedirectToWhatsApp(contract) {
  const mediationType = getMediationType(null, contract);
  
  if (mediationType !== "CONTEMPLADO COM BEM") {
    return { shouldRedirect: false, reason: "not_contemplado_com_bem" };
  }

  const installments = contract.installments || [];
  const hasOverdue30Days = installments.some(inst => {
    const c_pctipd = getInstallmentC_PCTIPD(inst);
    const daysDelay = inst.daysDelay || contract.daysDelay || 0;
    
    // Verifica se é C_PCTIPD=1 ou C_PCTIPD=2 com mais de 30 dias de atraso
    if ((c_pctipd === "1" || c_pctipd === "2") && daysDelay > 30) {
      return true;
    }
    return false;
  });

  return {
    shouldRedirect: hasOverdue30Days,
    reason: hasOverdue30Days ? "overdue_more_than_30_days" : "no_overdue_30_days"
  };
}

/**
 * Obtém a mensagem de erro baseada no motivo
 * @param {string} reason - Motivo da restrição
 * @returns {string} Mensagem de erro
 */
export function getErrorMessage(reason) {
  const messages = {
    "only_most_recent_can_be_unchecked": "Você só pode desmarcar a parcela mais recente selecionada.",
    "only_one_can_be_unchecked": "Você só pode desmarcar uma parcela. A parcela mais antiga deve permanecer selecionada.",
    "single_installment_cannot_be_unchecked": "Esta parcela deve permanecer selecionada.",
    "oldest_installment_cannot_be_unchecked": "A parcela mais atrasada deve permanecer selecionada. Este pagamento é obrigatório.",
    "all_type2_no_uncheck_allowed": "Não é possível desmarcar parcelas para este contrato.",
    "type2_must_always_be_selected": "A diferença de parcela (C_PCTIPD=2) deve estar sempre selecionada quando há mais de uma parcela selecionada.",
    "type2_must_be_selected_when_type1_selected": "A diferença de parcela (C_PCTIPD=2) deve estar selecionada quando há parcelas regulares (C_PCTIPD=1) selecionadas.",
    "type2_cannot_be_unchecked": "A diferença de parcela não pode ser desmarcada.",
    "all_type2_or_mixed_no_uncheck_allowed": "Não é possível desmarcar parcelas para este contrato.",
    "default": "Não é possível realizar esta ação."
  };

  return messages[reason] || messages["default"];
}

